import {call, put, takeLatest, Effect, select} from "redux-saga/effects";

import * as types from "./handlers";
import * as authTypes from "../auth/handlers";

import * as httpPatient from "../../http/patient";

import ShippingAddress from "../../model/ShippingAddress";
import SelectedProduct from "../../model/SelectedProduct";
import BillingCard from "../../model/BillingCard";

import MessageConst from "../../const/Message.const";

import {ActionType} from "../types";
import {getProfile, getSelectedProducts} from "../selectors";

function* onAddShippingAddress({payload}: ActionType): Generator<any, void, any> {
	const {selectNewAddress, mode, setPrimary, ...address} = payload;

	try {
		
		const result = yield call(httpPatient.addShippingAddress, address);
		const newShippingAddress = result.data;
		const profile = yield select(getProfile);
		const newProfile = {...profile};
		let shippingAddresses = [...newProfile.shippingAddresses, newShippingAddress];
		newProfile.shippingAddresses = shippingAddresses;

		yield put(authTypes.updateProfile(newProfile));

		if (mode === "checkout" || setPrimary) {
			yield put(types.setPrimaryShippingAddressRequest(result.data.id));
		}

		if (selectNewAddress) {
			yield put(types.selectShippingAddress(newShippingAddress));
		}

		yield put(types.addShippingAddressSuccess());
	} catch (error) {
		yield put(types.addShippingAddressFailure());
	}
}

function* onAddBillingCard({payload}: ActionType): Generator<any, void, any> {
	const {selectNewCard, mode, setPrimary, info} = payload;

	try {
		const result = yield call(httpPatient.addBillingCard, info);
		const newBillingCard = result.data;
		const profile = yield select(getProfile);
		const newProfile = {...profile};
		let billingCards = [...newProfile.billingCards, newBillingCard];
		newProfile.billingCards = billingCards;

		yield put(authTypes.updateProfile(newProfile));

		if (mode === "checkout" || setPrimary) {
			yield put(types.setPrimaryBillingCardRequest(result.data.id));
		}
		if (selectNewCard) {
			yield put(types.selectBillingCard(newBillingCard));
		}
		yield put(types.addBillingCardSuccess());
	} catch (error: any) {
		const message =
			error?.response?.data?.detail ||
			error?.response?.data?.message ||
			"An unexpected error occurred";
		yield put(types.setValidationMessage(message));
		yield put(types.addBillingCardFailure());
	}
}

function* onSetPrimaryShippingAddress({payload: id}: ActionType): Generator<any, void, any> {
	try {
		yield call(httpPatient.setPrimaryShippingAddress, id);

		const profile = yield select(getProfile);
		const newProfile = {...profile};

		newProfile.shippingAddresses = newProfile.shippingAddresses.map((address: ShippingAddress) => ({
			...address,
			isPrimary: false
		}));

		const addressIndex = newProfile.shippingAddresses.findIndex(
			(address: ShippingAddress) => address.id === id
		);

		newProfile.shippingAddresses[addressIndex].isPrimary = true;

		yield put(authTypes.updateProfile(newProfile));

		yield put(types.setPrimaryShippingAddressSuccess());
	} catch (error) {
		yield put(types.setPrimaryShippingAddressFailure());
	}
}

function* onSetPrimaryBillingCard({payload: id}: ActionType): Generator<any, void, any> {
	try {
		yield call(httpPatient.setPrimaryBillingCard, id);

		const profile = yield select(getProfile);
		const newProfile = {...profile};

		newProfile.billingCards = newProfile.billingCards.map((card: BillingCard) => ({
			...card,
			isPrimary: false
		}));

		const cardIndex = newProfile.billingCards.findIndex((card: ShippingAddress) => card.id === id);

		newProfile.billingCards[cardIndex].isPrimary = true;

		yield put(authTypes.updateProfile(newProfile));

		yield put(types.setPrimaryBillingCardSuccess());
	} catch (error) {
		yield put(types.setPrimaryBillingCardFailure());
	}
}

function* onVerifyPromoCode({payload}: ActionType): Generator<any, void, any> {
	const {promoCode} = payload;

	try {
		const result = yield call(httpPatient.verifyPromoCode, promoCode);
		const newPromoCode = result.data;
		if (newPromoCode.type === "GIFT") {
			const promocodeCategories = newPromoCode.problemCategory;
			let cartCategories: string[] = [];
			const cart = yield select(getSelectedProducts);

			cart.forEach((cartItem: SelectedProduct) =>
				cartCategories.push(cartItem.product.problemCategory)
			);

			cartCategories = cartCategories.filter(function (item, pos) {
				return cartCategories.indexOf(item) === pos;
			});

			const isIncludes = promocodeCategories.some((c: string) => cartCategories.includes(c));

			if (!isIncludes) {
				yield put(types.setPromoValidation({type: "error", text: MessageConst.error.promoCode}));
				yield put(types.verifyPromoCodeFailure());
				return;
			}
		}
		yield put(types.setPromoValidation({type: "success", text: MessageConst.promoCode.done}));
		yield put(types.verifyPromoCodeSuccess(newPromoCode));
	} catch (error) {
		yield put(types.setPromoValidation({type: "error", text: MessageConst.error.promoCode}));
		yield put(types.verifyPromoCodeFailure());
	}
}

function* onUpdateProfile({payload}: ActionType): Generator<any, void, any> {
	const {profile} = payload;
	try {
		const result = yield call(httpPatient.updateProfile, profile);
		const newProfile = result.data;
		
		yield put(authTypes.setValidationMessage(""));
		yield put(authTypes.updateProfile(newProfile));

		// if (callback) {
		// 	callback();
		// }
		
		yield put(types.updateProfileSuccess());
	} catch (error) {
		yield put(types.updateProfileFailure());
	}
}

function* onUploadId({payload: file}: ActionType): Generator<any, void, any> {
	try {
		let formData = new FormData();
		formData.append("file", file);

		const result = yield call(httpPatient.uploadId, formData);
		const newIdDocument = result.data;
		const profile = yield select(getProfile);
		const newProfile = {...profile, idDocument: newIdDocument};

		yield put(authTypes.updateProfile(newProfile));
		yield put(types.uploadIdSuccess());
	} catch (error) {
		yield put(types.uploadIdFailure());
	}
}

function* onUploadInsurance({payload: file}: ActionType): Generator<any, void, any> {
	try {
		let formData = new FormData();
		formData.append("file", file);

		const result = yield call(httpPatient.uploadInsurance, formData);
		const newIdUnsurance = result.data;
		const profile = yield select(getProfile);
		const newProfile = {...profile, insuranceDocument: newIdUnsurance};

		yield put(authTypes.updateProfile(newProfile));
		yield put(types.uploadInsuranceSuccess());
	} catch (error) {
		yield put(types.uploadInsuranceFailure());
	}
}

function* onSelectShippingAddress({payload}: ActionType) {
	try {
		if (payload?.id) {
			yield put(types.setPrimaryShippingAddressRequest(payload.id));
		}
	} catch (error) {
		console.log(error);
	}
}

function* onSelectBillingCard({payload}: ActionType) {
	try {
		if (payload?.id) {
			yield put(types.setPrimaryBillingCardRequest(payload.id));
		}
	} catch (error) {
		console.log(error);
	}
}

const patientSagas: Effect[] = [
	takeLatest(types.addShippingAddressRequest, onAddShippingAddress),
	takeLatest(types.addBillingCardRequest, onAddBillingCard),
	takeLatest(types.setPrimaryShippingAddressRequest, onSetPrimaryShippingAddress),
	takeLatest(types.setPrimaryBillingCardRequest, onSetPrimaryBillingCard),
	takeLatest(types.verifyPromoCodeRequest, onVerifyPromoCode),
	takeLatest(types.updateProfileRequest, onUpdateProfile),
	takeLatest(types.uploadIdRequest, onUploadId),
	takeLatest(types.uploadInsuranceRequest, onUploadInsurance),
	takeLatest(types.selectShippingAddress, onSelectShippingAddress),
	takeLatest(types.selectBillingCard, onSelectBillingCard)
];

export default patientSagas;
